import React, { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Artplayer from "artplayer";
import Hls from "hls.js";
import BASE_URL from "../../configs/api";
import { HiLockOpen, HiLockClosed } from "react-icons/hi";

const VideoPlayer = () => {
  const navigate = useNavigate();

  const { slug, tapId } = useParams();
  const [searchParams] = useSearchParams();
  const ngonngu = searchParams.get("ngonngu");
  const idTuUrl = searchParams.get("id");

  const [videoUrl, setVideoUrl] = useState(null);
  const [id, setId] = useState(idTuUrl || null);
  const [progress, setProgress] = useState(0);
  const [isLocked, setIsLocked] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [banner, setBanner] = useState("");
  const [tenPhim, setTenPhim] = useState("");
  const [episodes, setEpisodes] = useState([]);
  const [maxSoTap, setMaxSoTap] = useState(0);

  const containerRef = useRef(null);
  const artRef = useRef(null);
  const soTap = parseInt(tapId);

  const hasAccessToken = () => {
    const token = localStorage.getItem("accessToken");
    return !!token;
  };

  useEffect(() => {
    if (!slug) return;

    const fetchBanner = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/api/phim/${slug}/`);
        setBanner(res.data.banner_url || "");
        setTenPhim(res.data.ten_phim || "");
      } catch (error) {
        console.error("Không thể lấy thông tin phim:", error);
      }
    };

    fetchBanner();
  }, [slug]);

  useEffect(() => {
    const artContainer = artRef.current?.template.$bottom;
    if (artContainer) {
      artContainer.style.display = isLocked ? "none" : "";
    }
  }, [isLocked]);

  useEffect(() => {
    if (!slug || !ngonngu) return;
    const fetchEpisodes = async () => {
      try {
        const res = await axios.get(
          `${BASE_URL}/api/video-nguon/phim/${slug}/${ngonngu}/`
        );
        const data = res.data.sort((a, b) => a.so_tap - b.so_tap);
        setEpisodes(data);
        setMaxSoTap(data[data.length - 1]?.so_tap || 0);
      } catch (error) {
        console.error("Không thể lấy danh sách tập:", error);
      }
    };
    fetchEpisodes();
  }, [slug, ngonngu]);

  const goToPreviousEpisode = () => {
    if (soTap > 1) {
      const prevEpisode = episodes.find((ep) => ep.so_tap === soTap - 1);
      if (prevEpisode) {
        navigate(
          `/xem-phim/${slug}/tap/${soTap - 1}?id=${encodeURIComponent(
            prevEpisode.id
          )}&ngonngu=${encodeURIComponent(ngonngu)}`
        );
      }
    }
  };

  const goToNextEpisode = () => {
    if (soTap < maxSoTap) {
      const nextEpisode = episodes.find((ep) => ep.so_tap === soTap + 1);
      if (nextEpisode) {
        navigate(
          `/xem-phim/${slug}/tap/${soTap + 1}?id=${encodeURIComponent(
            nextEpisode.id
          )}&ngonngu=${encodeURIComponent(ngonngu)}`
        );
      }
    }
  };

  useEffect(() => {
    if (!slug || !soTap || !ngonngu) return;

    const fetchVideo = async () => {
      try {
        const res = await axios.get(
          `${BASE_URL}/api/video-nguon/phim/${slug}/tap/${soTap}/${ngonngu}/`
        );
        const originalLink = res.data.link_video;

        // Gọi API Django đã xử lý playlist
        const playlistRes = await axios.get(
          `${BASE_URL}/api/filtervideo/filtered-playlist/`,
          { params: { url: originalLink } }
        );

        const blob = new Blob([playlistRes.data], {
          type: "application/vnd.apple.mpegurl",
        });
        const blobUrl = URL.createObjectURL(blob);
        setVideoUrl(blobUrl);
        setId(res.data.id);
      } catch (error) {
      } finally {
      }
    };

    fetchVideo();
  }, [slug, soTap, ngonngu]);

  const fetchProgressFromAPI = async (videoId) => {
    if (!hasAccessToken() || !videoId) return null;

    const token = localStorage.getItem("accessToken");
    try {
      const response = await axios.get(
        `${BASE_URL}/api/video-progress/get_progress/`,
        {
          params: { video_nguon_id: videoId },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data.progress || null;
    } catch (error) {
      console.error("Lỗi khi lấy progress:", error);
      return null;
    }
  };

  const saveProgressToDb = async (progress, duration) => {
    if (!hasAccessToken() || !id) return;

    const token = localStorage.getItem("accessToken");
    try {
      await axios.post(
        `${BASE_URL}/api/video-progress/save_progress/`,
        { video_nguon_id: id, progress, duration },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
    } catch (error) {
      console.error("Lỗi khi lưu progress:", error);
    }
  };

  useEffect(() => {
    if (!videoUrl || !id || !containerRef.current) return;

    if (artRef.current) {
      artRef.current.destroy();
      artRef.current = null;
    }

    artRef.current = new Artplayer({
      container: containerRef.current,
      url: videoUrl,
      type: "m3u8",
      poster: banner,
      volume: 1,
      isLive: false,
      autoplay: false,
      pip: true,
      setting: true,
      loop: true,
      flip: true,
      playbackRate: true,
      aspectRatio: true,
      fullscreen: true,
      mutex: true,
      backdrop: true,
      playsInline: true,
      airplay: true,
      notice: true,
      theme: "#ff0000",
      lang: "vi-VN",
      moreVideoAttr: {
        crossOrigin: "anonymous",
      },
      controls: [
        // Rewind 10s Button
        {
          position: "left",
          html: `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M5.439.501a1.5 1.5 0 0 1 1.499 1.07l.033.134l.191.953a10.46 10.46 0 0 1 7.15-.926c5.656 1.275 9.209 6.895 7.934 12.552s-6.895 9.21-12.552 7.934C4.818 21.12 1.508 16.795 1.501 12a1.5 1.5 0 0 1 3-.004a7.5 7.5 0 1 0 1.66-4.727a1.5 1.5 0 0 1-2.666-.975l.01-.142l.505-4.325A1.5 1.5 0 0 1 5.44.5Zm3.769 6.677A1.5 1.5 0 0 1 10 8.5v7a1.5 1.5 0 0 1-3 0V11a1.5 1.5 0 0 1-.832-2.748l1.5-1a1.5 1.5 0 0 1 1.54-.074Zm5.292-.178A3.5 3.5 0 0 1 18 10.5v3a3.5 3.5 0 0 1-6.995.192L11 13.5v-3a3.5 3.5 0 0 1 3.5-3Zm0 3a.5.5 0 0 0-.492.41L14 10.5v3a.5.5 0 0 0 .992.09L15 13.5v-3a.5.5 0 0 0-.5-.5Z" /></svg>`,
          index: 19,
          tooltip: "Lùi 10s",
          click: function () {
            artRef.current.currentTime = Math.max(
              0,
              artRef.current.currentTime - 10
            );
          },
        },
        // Forward 10s Button
        {
          position: "left",
          html: '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"><g fill="none"><path fill="currentColor" d="M18.561.501a1.5 1.5 0 0 1 1.429 1.325l.505 4.325a1.5 1.5 0 0 1-2.657 1.117a7.5 7.5 0 1 0 1.66 4.727a1.5 1.5 0 0 1 3.001.004c-.007 4.795-3.317 9.12-8.193 10.22c-5.657 1.274-11.277-2.278-12.552-7.935C.48 8.627 4.032 3.007 9.69 1.732a10.46 10.46 0 0 1 7.149.926l.191-.953A1.5 1.5 0 0 1 18.561.5M9.208 7.178A1.5 1.5 0 0 1 10 8.5v7a1.5 1.5 0 1 1-3 0V11a1.5 1.5 0 0 1-.832-2.748l1.5-1a1.5 1.5 0 0 1 1.54-.074M14.5 7a3.5 3.5 0 0 1 3.495 3.308L18 10.5v3a3.5 3.5 0 0 1-6.995.192L11 13.5v-3A3.5 3.5 0 0 1 14.5 7m0 3a.5.5 0 0 0-.492.41L14 10.5v3a.5.5 0 0 0 .992.09L15 13.5v-3a.5.5 0 0 0-.5-.5"/></g></svg>',
          index: 20,
          tooltip: "Tua 10s",
          click: function () {
            artRef.current.currentTime = Math.min(
              artRef.current.duration,
              artRef.current.currentTime + 10
            );
          },
        },

        // Previous Episode Button
        {
          position: "left",
          html: `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"><path fill="currentColor" d="M3.885 21.06a.76.76 0 0 1-.75-.75V3.69a.75.75 0 0 1 1.5 0v16.6a.75.75 0 0 1-.75.77m16.98-15.713v13.25a2.35 2.35 0 0 1-.32 1.13a2.2 2.2 0 0 1-1.89 1.07h-.1a2.1 2.1 0 0 1-1.11-.36l-9.13-6.12a2.25 2.25 0 0 1-.71-.76a2.3 2.3 0 0 1-.27-1a2.2 2.2 0 0 1 .2-1a2.2 2.2 0 0 1 .64-.81l9.14-7.09a2.2 2.2 0 0 1 1.13-.44a2.2 2.2 0 0 1 2.09 1.02c.204.335.318.718.33 1.11"/></svg>`,
          index: 21,
          tooltip: `Tập trước (${soTap - 1})`,
          click: goToPreviousEpisode,
          style: {
            opacity: soTap <= 1 ? 0.3 : 1,
            pointerEvents: soTap <= 1 ? "none" : "auto",
          },
        },
        // Next Episode Button
        {
          position: "left",
          html: `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"><path fill="currentColor" d="M20.095 21a.75.75 0 0 1-.75-.75V3.75a.75.75 0 0 1 1.5 0v16.5a.74.74 0 0 1-.75.75m-3.4-9.589a2.25 2.25 0 0 1-.85 1.82l-9.11 7.09c-.326.247-.713.4-1.12.44h-.23a2.14 2.14 0 0 1-1-.22a2.2 2.2 0 0 1-.9-.81a2.17 2.17 0 0 1-.33-1.16V5.421a2.2 2.2 0 0 1 .31-1.12a2.25 2.25 0 0 1 .85-.8a2.18 2.18 0 0 1 2.24.1l9.12 6.08c.29.191.53.448.7.75a2.3 2.3 0 0 1 .32.98"/></svg>`,
          index: 22,
          tooltip: `Tập tiếp theo (${soTap + 1})`,
          click: goToNextEpisode,
          style: {
            opacity: soTap >= maxSoTap ? 0.3 : 1,
            pointerEvents: soTap >= maxSoTap ? "none" : "auto",
          },
        },
      ],
      customType: {
        m3u8: function (video, url) {
          if (Hls.isSupported()) {
            const hls = new Hls();
            hls.loadSource(url);
            hls.attachMedia(video);
          } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
            video.src = url;
          }
        },
      },
    });

    const art = artRef.current;

    art.on("ready", () => {
      const container = art.template.$video;
      let lastTap = 0;
      const seekTime = 10;

      container.addEventListener("touchend", (e) => {
        const currentTime = new Date().getTime();
        const tapLength = currentTime - lastTap;
        const rect = container.getBoundingClientRect();
        const x = e.changedTouches[0].clientX - rect.left;
        const isLeft = x < rect.width / 2;

        if (tapLength < 300 && tapLength > 0) {
          e.preventDefault();

          if (isLeft) {
            art.seek = Math.max(0, art.currentTime - seekTime);
            art.emit("notice", {
              text: `Lùi ${seekTime}s`,
              duration: 2000,
            });
          } else {
            art.seek = Math.min(art.duration, art.currentTime + seekTime);
            art.emit("notice", {
              text: `Tiến ${seekTime}s`,
              duration: 2000,
            });
          }

          art.play();
        }

        lastTap = currentTime;
      });
    });

    fetchProgressFromAPI(id).then((apiProgress) => {
      if (apiProgress !== null) {
        art.currentTime = apiProgress;
        setProgress(apiProgress);
      }
    });

    const handleUnload = () => {
      saveProgressToDb(art.currentTime, art.duration);
    };

    art.on("timeupdate", () => {
      setProgress(art.currentTime);
    });

    window.addEventListener("beforeunload", handleUnload);

    return () => {
      handleUnload();
      window.removeEventListener("beforeunload", handleUnload);
      if (artRef.current) {
        artRef.current.destroy();
        artRef.current = null;
      }
    };
  }, [videoUrl, id]);

  const handleMouseEnter = () => setIsVisible(true);
  const handleMouseLeave = () => setIsVisible(false);

  if ((!videoUrl || !id) && banner) {
    return (
      <div className="relative w-full aspect-[16/10] lg:aspect-[16/7] bg-black rounded-t-lg overflow-hidden">
        <img src={banner} alt="banner" className="w-full h-full object-cover" />
      </div>
    );
  }

  return (
    <div
      className="relative w-full aspect-[16/10] lg:aspect-[16/7] bg-black rounded-t-sm overflow-hidden group"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div ref={containerRef} className="w-full h-full" />

      {isVisible && (
        <button
          onClick={() => setIsLocked((prev) => !prev)}
          className="absolute ml-2 top-1/2 transform -translate-y-1/2 px-4 z-20  text-white text-sm "
        >
          {isLocked ? <HiLockClosed size={20} /> : <HiLockOpen size={20} />}
        </button>
      )}

      <div
        className={`absolute top-10 left-10 z-10 transition-opacity duration-300 ${
          isVisible ? "opacity-100" : "opacity-0"
        }`}
      >
        <div className="text-white px-3 text-sm border-l-4 border-orange-500">
          <div className="hidden sm:block font-bold text-lg drop-shadow-lg">
            {tenPhim} - Tập {soTap}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoPlayer;
